import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Sparkles,
  Upload,
  Image as ImageIcon,
  Video,
  Eye,
  Trash2,
  Pin,
  Clock,
  Shield,
  Users,
  Globe,
  Star,
  RefreshCw,
  Send,
  CheckCircle2,
  AlertCircle,
  Play,
  Pause,
  X,
  Plus,
  Sliders,
  Check,
  Flame,
  Hash,
  Smile,
  ChevronRight,
  Info
} from "lucide-react";
import { TelegramAccount, TelegramStory } from "../types";

export const getAccountPhone = (a: any): string => {
  if (!a) return "";
  return a.phoneNumber || a.phone || a.id || "";
};

export const normalizePhone = (phone: string): string => {
  if (!phone) return "";
  return String(phone).replace(/\D/g, "");
};

export const isSamePhone = (p1?: string, p2?: string): boolean => {
  if (!p1 || !p2) return false;
  if (p1 === p2) return true;
  const n1 = normalizePhone(p1);
  const n2 = normalizePhone(p2);
  return n1.length > 0 && n1 === n2;
};

export const getAccountDisplayName = (a: any): string => {
  if (!a) return "未命名账号";
  const name = [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (a.username) return `@${a.username.replace(/^@/, "")}`;
  return getAccountPhone(a) || "Telegram 账号";
};

interface StoriesViewProps {
  accounts: TelegramAccount[];
  selectedAccountPhone?: string;
  onSelectAccount?: (phone: string) => void;
  token?: string;
  currentUser?: any;
}

export const StoriesView: React.FC<StoriesViewProps> = ({
  accounts,
  selectedAccountPhone,
  onSelectAccount,
  token,
  currentUser
}) => {
  const connectedAccounts = useMemo(() => {
    return accounts.filter(a => {
      if (!a) return false;
      const phone = getAccountPhone(a);
      if (!phone) return false;
      return a.status === "connected" || a.status === "active" || a.status === "online" || !!(a.sessionString || (a as any).hasSession);
    });
  }, [accounts]);

  // Sub-tabs: "publish" | "manage"
  const [activeSubTab, setActiveSubTab] = useState<"publish" | "manage">("publish");

  // Publish Form State
  const [selectedPhones, setSelectedPhones] = useState<string[]>(() => {
    if (selectedAccountPhone) return [selectedAccountPhone];
    const first = connectedAccounts[0];
    return first ? [getAccountPhone(first)] : [];
  });
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string>("");
  const [mediaType, setMediaType] = useState<"photo" | "video">("photo");
  const [caption, setCaption] = useState<string>("");
  const [period, setPeriod] = useState<number>(86400); // 6h=21600, 12h=43200, 24h=86400, 48h=172800
  const [privacy, setPrivacy] = useState<"all" | "contacts" | "close_friends">("all");
  const [pinned, setPinned] = useState<boolean>(false);
  const [noforwards, setNoforwards] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [publishLogs, setPublishLogs] = useState<{ phone: string; success: boolean; message: string }[]>([]);

  // Stories Management State
  const [inspectPhone, setInspectPhone] = useState<string>(() => {
    if (selectedAccountPhone) return selectedAccountPhone;
    const first = connectedAccounts[0];
    return first ? getAccountPhone(first) : "";
  });
  const [storiesList, setStoriesList] = useState<TelegramStory[]>([]);
  const [isLoadingStories, setIsLoadingStories] = useState<boolean>(false);

  // Fullscreen Story Preview Modal
  const [previewStory, setPreviewStory] = useState<TelegramStory | null>(null);
  const [storyProgress, setStoryProgress] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isAccountSelected = (acc: TelegramAccount | any): boolean => {
    const accPhone = getAccountPhone(acc);
    return selectedPhones.some(p => isSamePhone(p, accPhone) || p === accPhone);
  };

  const selectedCount = connectedAccounts.filter(acc => isAccountSelected(acc)).length;

  // Sync selected account when props or accounts list change
  useEffect(() => {
    if (selectedAccountPhone) {
      const match = connectedAccounts.find(a => isSamePhone(getAccountPhone(a), selectedAccountPhone));
      const targetPhone = match ? getAccountPhone(match) : selectedAccountPhone;
      
      setSelectedPhones(prev => {
        // Clean out invalid / phantom phones
        const valid = prev.filter(p => connectedAccounts.some(a => isSamePhone(getAccountPhone(a), p)));
        if (!valid.some(p => isSamePhone(p, targetPhone))) {
          return [targetPhone];
        }
        return valid;
      });
      setInspectPhone(targetPhone);
    } else if (connectedAccounts.length > 0) {
      setSelectedPhones(prev => {
        const valid = prev.filter(p => connectedAccounts.some(a => isSamePhone(getAccountPhone(a), p)));
        if (valid.length === 0) {
          return [getAccountPhone(connectedAccounts[0])];
        }
        return valid;
      });
      setInspectPhone(prev => prev || getAccountPhone(connectedAccounts[0]));
    } else {
      setSelectedPhones([]);
      setInspectPhone("");
    }
  }, [selectedAccountPhone, connectedAccounts]);

  // Load stories when inspectPhone changes or subtab switches to manage
  useEffect(() => {
    if (inspectPhone && activeSubTab === "manage") {
      fetchStoriesForAccount(inspectPhone);
    }
  }, [inspectPhone, activeSubTab]);

  const fetchStoriesForAccount = async (phone: string) => {
    if (!phone) return;
    setIsLoadingStories(true);
    try {
      const res = await fetch(`/api/stories/list?phone=${encodeURIComponent(phone)}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ""
        }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.stories)) {
        setStoriesList(data.stories);
      } else {
        setStoriesList([]);
      }
    } catch (err) {
      console.error("Failed to fetch stories:", err);
      setStoriesList([]);
    } finally {
      setIsLoadingStories(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processMediaFile(file);
  };

  const processMediaFile = (file: File) => {
    setMediaFile(file);
    const isVid = file.type.startsWith("video/");
    setMediaType(isVid ? "video" : "photo");

    const reader = new FileReader();
    reader.onload = (ev) => {
      setMediaPreviewUrl(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processMediaFile(e.dataTransfer.files[0]);
    }
  };

  const handleToggleAccountSelection = (accPhone: string) => {
    if (!accPhone) return;
    setSelectedPhones(prev => {
      const isSelected = prev.some(p => isSamePhone(p, accPhone) || p === accPhone);
      if (isSelected) {
        return prev.filter(p => !isSamePhone(p, accPhone) && p !== accPhone);
      } else {
        return [...prev.filter(p => !isSamePhone(p, accPhone) && p !== accPhone), accPhone];
      }
    });
  };

  const handleSelectAllAccounts = () => {
    if (selectedCount === connectedAccounts.length && connectedAccounts.length > 0) {
      setSelectedPhones([]);
    } else {
      setSelectedPhones(connectedAccounts.map(a => getAccountPhone(a)).filter(Boolean));
    }
  };

  const handlePublishStory = async () => {
    const targetPhones = connectedAccounts
      .filter(a => isAccountSelected(a))
      .map(a => getAccountPhone(a));

    if (targetPhones.length === 0) {
      alert("请至少选择一个用于发布动态的 Telegram 账号");
      return;
    }
    if (!mediaPreviewUrl) {
      alert("请上传动态图片或视频");
      return;
    }

    setIsPublishing(true);
    setPublishLogs([]);

    try {
      const payload = {
        phones: targetPhones,
        mediaBase64: mediaPreviewUrl,
        mediaType,
        caption,
        period,
        privacy,
        pinned,
        noforwards,
        fileName: mediaFile?.name || (mediaType === "video" ? "story.mp4" : "story.jpg")
      };

      const res = await fetch("/api/stories/publish", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : ""
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.results && Array.isArray(data.results)) {
        const logs = data.results.map((r: any) => ({
          phone: r.phone,
          success: r.success,
          message: r.success ? `发布成功 (Story ID: ${r.storyId})` : `发布失败: ${r.error || "未知错误"}`
        }));
        setPublishLogs(logs);

        if (data.success) {
          // Reset form on full success
          setTimeout(() => {
            if (inspectPhone) fetchStoriesForAccount(inspectPhone);
          }, 1000);
        }
      } else {
        setPublishLogs([{ phone: "系统", success: false, message: data.error || "发布接口请求异常" }]);
      }
    } catch (err: any) {
      setPublishLogs([{ phone: "系统", success: false, message: err.message || "网络请求失败" }]);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleTogglePinnedStory = async (storyId: number, currentPinned: boolean) => {
    try {
      const res = await fetch("/api/stories/toggle-pinned", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : ""
        },
        body: JSON.stringify({
          phone: inspectPhone,
          storyId,
          pinned: !currentPinned
        })
      });
      const data = await res.json();
      if (data.success) {
        setStoriesList(prev => prev.map(s => s.id === storyId ? { ...s, pinned: !currentPinned } : s));
      }
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  const handleDeleteStory = async (storyId: number) => {
    if (!confirm("确定要删除这条 Telegram 动态吗？删除后将从 Telegram 服务器和主页同步移除。")) {
      return;
    }
    try {
      const res = await fetch("/api/stories/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : ""
        },
        body: JSON.stringify({
          phone: inspectPhone,
          storyIds: [storyId]
        })
      });
      const data = await res.json();
      if (data.success) {
        setStoriesList(prev => prev.filter(s => s.id !== storyId));
        if (previewStory?.id === storyId) setPreviewStory(null);
      }
    } catch (err) {
      console.error("Failed to delete story:", err);
    }
  };

  // Story Fullscreen auto-progress timer
  useEffect(() => {
    let interval: any = null;
    if (previewStory && isPlaying) {
      interval = setInterval(() => {
        setStoryProgress(prev => {
          if (prev >= 100) {
            setPreviewStory(null);
            return 0;
          }
          return prev + 1.5;
        });
      }, 70);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [previewStory, isPlaying]);

  const quickEmojis = ["🔥", "✨", "🎉", "🚀", "❤️", "📢", "🌟", "💡", "🎯", "💎"];
  const quickTags = ["#Telegram", "#Official", "#Story", "#Update", "#Promo", "#Daily"];

  const currentPreviewAccount = connectedAccounts.find(a => isAccountSelected(a)) || connectedAccounts[0];

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-950 text-slate-100 overflow-hidden">
      {/* Header Banner */}
      <div className="border-b border-slate-800/80 bg-slate-900/60 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-sky-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-sky-500/20 ring-1 ring-white/10">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-slate-100 tracking-tight">Telegram 动态发布与管理</h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30">
                Official Stories API
              </span>
            </div>
            <p className="text-xs text-slate-400">
              与 Telegram 官方功能完全一致：支持全屏图文/视频、自定义可见性、自动过期、个人主页置顶与防截屏保护
            </p>
          </div>
        </div>

        {/* Sub-tab Navigation */}
        <div className="flex items-center p-1 bg-slate-900 border border-slate-800 rounded-xl">
          <button
            type="button"
            onClick={() => setActiveSubTab("publish")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "publish"
                ? "bg-sky-500 text-white shadow-sm shadow-sky-500/30"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Send className="w-3.5 h-3.5" />
            发布动态工作台
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab("manage")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "manage"
                ? "bg-sky-500 text-white shadow-sm shadow-sky-500/30"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            已发布动态与主页档案 ({storiesList.length})
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeSubTab === "publish" ? (
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Column: Form & Configuration (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* 1. Account Selector Card */}
              <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <Users className="w-4 h-4 text-sky-400" />
                    选择发布账号 ({selectedCount}/{connectedAccounts.length})
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAllAccounts}
                    className="text-xs text-sky-400 hover:text-sky-300 font-medium cursor-pointer"
                  >
                    {selectedCount === connectedAccounts.length && connectedAccounts.length > 0 ? "取消全选" : "全选所有在线账号"}
                  </button>
                </div>

                {connectedAccounts.length === 0 ? (
                  <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    当前无在线 Telegram 账号，请先在「账号管理」中连接或导入账号。
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                    {connectedAccounts.map(acc => {
                      const phone = getAccountPhone(acc);
                      const isSelected = isAccountSelected(acc);
                      const displayName = getAccountDisplayName(acc);
                      return (
                        <div
                          key={phone}
                          onClick={() => handleToggleAccountSelection(phone)}
                          className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${
                            isSelected
                              ? "bg-sky-500/10 border-sky-500/40 text-slate-100"
                              : "bg-slate-950/40 border-slate-800/80 text-slate-400 hover:border-slate-700"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                              isSelected
                                ? "bg-sky-500 border-sky-500 text-white"
                                : "border-slate-700 bg-slate-900"
                            }`}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold truncate text-slate-200">{displayName}</div>
                            <div className="text-[10px] text-slate-500 font-mono truncate">{phone}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 2. Media Upload Card */}
              <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-purple-400" />
                    动态多媒体内容 (图片 / 视频)
                  </label>
                  {mediaPreviewUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        setMediaFile(null);
                        setMediaPreviewUrl("");
                      }}
                      className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      清空文件
                    </button>
                  )}
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
                  className="hidden"
                />

                {!mediaPreviewUrl ? (
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-800 hover:border-sky-500/50 hover:bg-sky-500/5 rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 group"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-slate-800 group-hover:bg-sky-500/20 group-hover:text-sky-400 text-slate-400 flex items-center justify-center mb-3 transition-colors">
                      <Upload className="w-7 h-7" />
                    </div>
                    <span className="text-sm font-semibold text-slate-200 group-hover:text-sky-300 mb-1">
                      点击上传或拖拽图片/视频至此处
                    </span>
                    <span className="text-xs text-slate-500 max-w-sm">
                      支持 JPG、PNG、GIF、WebP 图片及 MP4 视频，标准 9:16 竖屏显示最佳
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div className="w-16 h-20 rounded-lg overflow-hidden bg-slate-900 border border-slate-800 relative shrink-0">
                      {mediaType === "video" ? (
                        <video src={mediaPreviewUrl} className="w-full h-full object-cover" />
                      ) : (
                        <img src={mediaPreviewUrl} alt="preview" className="w-full h-full object-cover" />
                      )}
                      <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/70 text-[9px] font-mono text-white">
                        {mediaType === "video" ? "MP4" : "IMG"}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-200 truncate">
                        {mediaFile?.name || "已选择媒体文件"}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {mediaFile ? `${(mediaFile.size / 1024 / 1024).toFixed(2)} MB` : "Base64 数据"}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-300 transition-colors"
                        >
                          更换文件
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Caption & Quick Inserters */}
              <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <Smile className="w-4 h-4 text-amber-400" />
                    动态配文 (Caption)
                  </label>
                  <span className="text-[11px] text-slate-500 font-mono">{caption.length}/1024</span>
                </div>

                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="写下关于这条动态的精彩说明、标签或联系方式..."
                  rows={3}
                  maxLength={1024}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950/80 border border-slate-800 text-xs text-slate-100 placeholder-slate-600 focus:outline-none focus:border-sky-500/60 focus:ring-1 focus:ring-sky-500/30 resize-none transition-all"
                />

                {/* Quick Emoji & Hashtags */}
                <div className="space-y-2 pt-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] text-slate-500 font-semibold mr-1 flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-400" /> 快捷表情:
                    </span>
                    {quickEmojis.map(em => (
                      <button
                        key={em}
                        type="button"
                        onClick={() => setCaption(prev => prev + em)}
                        className="w-6 h-6 rounded-md bg-slate-800/80 hover:bg-slate-700 text-xs flex items-center justify-center transition-colors cursor-pointer"
                      >
                        {em}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] text-slate-500 font-semibold mr-1 flex items-center gap-1">
                      <Hash className="w-3 h-3 text-sky-400" /> 话题标签:
                    </span>
                    {quickTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setCaption(prev => (prev ? `${prev} ${tag}` : tag))}
                        className="px-2 py-0.5 rounded-md bg-slate-800/80 hover:bg-sky-500/20 hover:text-sky-300 text-[10px] text-slate-400 font-mono transition-colors cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 4. Official Settings (Period, Privacy, Pinned, Protect) */}
              <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-sm space-y-4">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-emerald-400" />
                  官方动态高级选项
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Period */}
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-sky-400" />
                      留存时长 (Story Period)
                    </span>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[
                        { label: "6小时", val: 21600 },
                        { label: "12小时", val: 43200 },
                        { label: "24小时", val: 86400 },
                        { label: "48小时", val: 172800 }
                      ].map(p => (
                        <button
                          key={p.val}
                          type="button"
                          onClick={() => setPeriod(p.val)}
                          className={`py-2 px-1 rounded-xl text-center text-xs font-medium transition-all ${
                            period === p.val
                              ? "bg-sky-500 text-white font-semibold shadow-sm shadow-sky-500/20"
                              : "bg-slate-950/60 text-slate-400 border border-slate-800 hover:border-slate-700"
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Privacy */}
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-purple-400" />
                      谁可以看到 (Privacy)
                    </span>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { label: "所有人", val: "all" },
                        { label: "联系人", val: "contacts" },
                        { label: "密友圈", val: "close_friends" }
                      ].map(pr => (
                        <button
                          key={pr.val}
                          type="button"
                          onClick={() => setPrivacy(pr.val as any)}
                          className={`py-2 px-1 rounded-xl text-center text-xs font-medium transition-all ${
                            privacy === pr.val
                              ? "bg-purple-600 text-white font-semibold shadow-sm shadow-purple-600/20"
                              : "bg-slate-950/60 text-slate-400 border border-slate-800 hover:border-slate-700"
                          }`}
                        >
                          {pr.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Switches: Pin to profile & Protect content */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div
                    onClick={() => setPinned(!pinned)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      pinned
                        ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                        : "bg-slate-950/40 border-slate-800/80 text-slate-400 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Pin className="w-4 h-4 text-amber-400" />
                      <div>
                        <div className="text-xs font-semibold">置顶至个人主页</div>
                        <div className="text-[10px] text-slate-500">动态到期后仍保留在主页档案</div>
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all ${
                        pinned ? "bg-amber-500 border-amber-500 text-black" : "border-slate-700 bg-slate-900"
                      }`}
                    >
                      {pinned && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>
                  </div>

                  <div
                    onClick={() => setNoforwards(!noforwards)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      noforwards
                        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                        : "bg-slate-950/40 border-slate-800/80 text-slate-400 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Shield className="w-4 h-4 text-emerald-400" />
                      <div>
                        <div className="text-xs font-semibold">禁止转发与截屏</div>
                        <div className="text-[10px] text-slate-500">开启内容保护防止外部扩散</div>
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all ${
                        noforwards ? "bg-emerald-500 border-emerald-500 text-black" : "border-slate-700 bg-slate-900"
                      }`}
                    >
                      {noforwards && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Submit Button & Live Feedback */}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handlePublishStory}
                  disabled={isPublishing || selectedCount === 0 || !mediaPreviewUrl}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-600 hover:from-sky-400 hover:via-indigo-400 hover:to-purple-500 disabled:opacity-50 text-white font-bold text-sm shadow-lg shadow-sky-500/25 flex items-center justify-center gap-2.5 transition-all cursor-pointer"
                >
                  {isPublishing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      正在向 Telegram DC 节点上传并发布动态...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      立即向选中的 {selectedCount} 个账号发表动态
                    </>
                  )}
                </button>

                {publishLogs.length > 0 && (
                  <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-2">
                    <div className="text-xs font-bold text-slate-300">发布结果反馈:</div>
                    {publishLogs.map((log, idx) => (
                      <div
                        key={idx}
                        className={`text-xs p-2 rounded-xl flex items-center gap-2 ${
                          log.success
                            ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                            : "bg-rose-500/10 text-rose-300 border border-rose-500/20"
                        }`}
                      >
                        {log.success ? (
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                        )}
                        <span className="font-mono">{log.phone}:</span>
                        <span>{log.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Live Telegram Mobile Preview (5 cols) */}
            <div className="lg:col-span-5 flex flex-col items-center">
              <div className="w-full max-w-[340px] sticky top-6">
                <div className="text-center mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    📱 Telegram 客户端实时效果仿真预览
                  </span>
                </div>

                {/* Smartphone Mock Frame */}
                <div className="w-full aspect-[9/16] bg-black rounded-[38px] p-3 shadow-2xl ring-1 ring-slate-800 relative overflow-hidden flex flex-col border-4 border-slate-800">
                  {/* Speaker Notch */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-4 bg-slate-900 rounded-full z-30" />

                  {/* Story Content Canvas */}
                  <div className="w-full h-full rounded-[28px] overflow-hidden relative bg-slate-950 flex flex-col justify-between">
                    {/* Media Background */}
                    {mediaPreviewUrl ? (
                      mediaType === "video" ? (
                        <video src={mediaPreviewUrl} autoPlay loop muted className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <img src={mediaPreviewUrl} alt="story preview" className="absolute inset-0 w-full h-full object-cover" />
                      )
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950 via-slate-900 to-black flex flex-col items-center justify-center p-6 text-center">
                        <ImageIcon className="w-12 h-12 text-slate-700 mb-2" />
                        <span className="text-xs text-slate-500">上传图片或视频后<br />在此实时呈现沉浸式画面</span>
                      </div>
                    )}

                    {/* Gradient Overlays for Readability */}
                    <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-black/80 via-black/30 to-transparent pointer-events-none z-10" />
                    <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none z-10" />

                    {/* Top Progress Bar & Header */}
                    <div className="relative z-20 p-3 pt-4 space-y-2">
                      {/* Multi-segment Progress Bar */}
                      <div className="w-full h-1 bg-white/30 rounded-full overflow-hidden">
                        <div className="h-full bg-white rounded-full w-2/3 animate-pulse" />
                      </div>

                      {/* Header Info */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-white font-bold text-xs ring-2 ring-white/60">
                            {(currentPreviewAccount?.firstName?.[0] || currentPreviewAccount?.username?.[0] || "T").toUpperCase()}
                          </div>
                          <div className="text-left">
                            <div className="text-xs font-bold text-white leading-tight drop-shadow-md">
                              {currentPreviewAccount?.firstName || currentPreviewAccount?.username || "Telegram 账号"}
                            </div>
                            <div className="text-[10px] text-white/70 flex items-center gap-1">
                              <span>刚刚发布</span>
                              <span>•</span>
                              <span>{period / 3600}小时内有效</span>
                            </div>
                          </div>
                        </div>

                        {/* Top Badges */}
                        <div className="flex items-center gap-1.5">
                          {pinned && (
                            <span className="p-1 rounded-full bg-amber-500/80 text-black text-[10px]" title="主页置顶">
                              <Pin className="w-3 h-3 fill-current" />
                            </span>
                          )}
                          {noforwards && (
                            <span className="p-1 rounded-full bg-emerald-500/80 text-white text-[10px]" title="禁止转发">
                              <Shield className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Caption & Interactions */}
                    <div className="relative z-20 p-4 space-y-2">
                      {caption && (
                        <div className="text-xs text-white leading-relaxed drop-shadow-md bg-black/30 backdrop-blur-xs p-2.5 rounded-xl border border-white/10 max-h-24 overflow-y-auto">
                          {caption}
                        </div>
                      )}

                      {/* Fake Telegram Story Footer (Send Message Pill) */}
                      <div className="flex items-center gap-2 pt-1">
                        <div className="flex-1 py-1.5 px-3 rounded-full bg-white/20 backdrop-blur-md border border-white/20 text-[11px] text-white/70">
                          发送私信回复...
                        </div>
                        <div className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-xs">
                          ❤️
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Manage Sub-tab: Published Stories Gallery */
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Account Switcher & Refresh */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-400 uppercase">查看账号动态:</span>
                <select
                  value={inspectPhone}
                  onChange={(e) => setInspectPhone(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                >
                  {connectedAccounts.map(a => {
                    const phone = getAccountPhone(a);
                    return (
                      <option key={phone} value={phone}>
                        {getAccountDisplayName(a)} ({phone})
                      </option>
                    );
                  })}
                </select>
              </div>

              <button
                type="button"
                onClick={() => fetchStoriesForAccount(inspectPhone)}
                disabled={isLoadingStories}
                className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 flex items-center gap-2 transition-colors cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoadingStories ? "animate-spin" : ""}`} />
                刷新动态列表
              </button>
            </div>

            {/* Stories Grid */}
            {isLoadingStories ? (
              <div className="p-16 flex flex-col items-center justify-center text-center text-slate-500">
                <RefreshCw className="w-8 h-8 animate-spin text-sky-400 mb-3" />
                <span className="text-xs">正在从 Telegram DC 节点加载动态数据...</span>
              </div>
            ) : storiesList.length === 0 ? (
              <div className="p-16 rounded-2xl border border-dashed border-slate-800 flex flex-col items-center justify-center text-center">
                <Sparkles className="w-12 h-12 text-slate-700 mb-3" />
                <h3 className="text-sm font-bold text-slate-300 mb-1">当前账号暂无已发表的动态</h3>
                <p className="text-xs text-slate-500 max-w-sm mb-4">
                  点击上方「发布动态工作台」即可立即发布全新图文/视频动态至 Telegram 平台。
                </p>
                <button
                  type="button"
                  onClick={() => setActiveSubTab("publish")}
                  className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold transition-all"
                >
                  前往发布第一条动态
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {storiesList.map((story) => {
                  const isExpired = story.expireAt ? story.expireAt < Date.now() : false;
                  const remainingHours = story.expireAt
                    ? Math.max(0, Math.round((story.expireAt - Date.now()) / (1000 * 3600)))
                    : 0;

                  return (
                    <div
                      key={story.id}
                      className="group bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden flex flex-col transition-all hover:border-sky-500/50 hover:shadow-lg relative"
                    >
                      {/* Thumbnail / Media Canvas */}
                      <div
                        onClick={() => {
                          setPreviewStory(story);
                          setStoryProgress(0);
                          setIsPlaying(true);
                        }}
                        className="aspect-[9/16] bg-slate-950 relative overflow-hidden cursor-pointer"
                      >
                        {story.mediaBase64 || story.mediaUrl ? (
                          story.mediaType === "video" ? (
                            <video
                              src={story.mediaBase64 || story.mediaUrl}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <img
                              src={story.mediaBase64 || story.mediaUrl}
                              alt="story"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-3 bg-gradient-to-b from-slate-900 to-slate-950 text-center">
                            <Sparkles className="w-8 h-8 text-sky-400/60 mb-2" />
                            <span className="text-[10px] text-slate-500">Telegram 官方动态</span>
                          </div>
                        )}

                        {/* Top Badges */}
                        <div className="absolute top-2 inset-x-2 flex items-center justify-between z-10">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                              !isExpired
                                ? "bg-emerald-500/80 text-white backdrop-blur-xs"
                                : "bg-slate-800/80 text-slate-400"
                            }`}
                          >
                            {!isExpired ? `活跃 (余${remainingHours}h)` : "已归档"}
                          </span>

                          {story.pinned && (
                            <span className="p-1 rounded-full bg-amber-500 text-black text-[9px]" title="已置顶主页">
                              <Pin className="w-3 h-3 fill-current" />
                            </span>
                          )}
                        </div>

                        {/* Bottom Stats Overlay */}
                        <div className="absolute bottom-0 inset-x-0 p-2.5 bg-gradient-to-t from-black/90 to-transparent z-10">
                          {story.caption && (
                            <p className="text-[11px] text-white font-medium line-clamp-2 leading-snug drop-shadow-sm mb-1">
                              {story.caption}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-[10px] text-slate-300">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3 text-sky-400" />
                              {story.viewsCount || 0}
                            </span>
                            <span className="font-mono text-slate-400">
                              {new Date(story.publishedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="p-2 border-t border-slate-800/80 flex items-center justify-between bg-slate-950/40">
                        <button
                          type="button"
                          onClick={() => handleTogglePinnedStory(story.id, story.pinned)}
                          className={`p-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors ${
                            story.pinned
                              ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                          }`}
                          title={story.pinned ? "取消主页置顶" : "置顶至主页档案"}
                        >
                          <Pin className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-semibold">{story.pinned ? "已置顶" : "置顶"}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteStory(story.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                          title="删除动态"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Story Interactive Player Modal */}
      {previewStory && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-[360px] aspect-[9/16] bg-black rounded-3xl overflow-hidden relative shadow-2xl border border-slate-800 flex flex-col justify-between">
            {/* Background Media */}
            {previewStory.mediaBase64 || previewStory.mediaUrl ? (
              previewStory.mediaType === "video" ? (
                <video src={previewStory.mediaBase64 || previewStory.mediaUrl} autoPlay loop muted className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <img src={previewStory.mediaBase64 || previewStory.mediaUrl} alt="story" className="absolute inset-0 w-full h-full object-cover" />
              )
            ) : (
              <div className="absolute inset-0 bg-gradient-to-b from-indigo-950 to-black flex items-center justify-center">
                <Sparkles className="w-16 h-16 text-sky-400" />
              </div>
            )}

            {/* Gradient Overlays */}
            <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-black/80 to-transparent pointer-events-none z-10" />
            <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-black/90 to-transparent pointer-events-none z-10" />

            {/* Header with Progress Bar */}
            <div className="relative z-20 p-4 space-y-3">
              <div className="w-full h-1 bg-white/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-75"
                  style={{ width: `${storyProgress}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-white font-bold text-xs ring-2 ring-white/60">
                    TG
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white">{inspectPhone}</div>
                    <div className="text-[10px] text-white/70">
                      {new Date(previewStory.publishedAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60"
                  >
                    {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewStory(null)}
                    className="p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Caption & Metrics */}
            <div className="relative z-20 p-4 space-y-2">
              {previewStory.caption && (
                <div className="text-xs text-white leading-relaxed p-3 rounded-xl bg-black/40 backdrop-blur-md border border-white/10">
                  {previewStory.caption}
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-white/80 pt-1">
                <span className="flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5 text-sky-400" />
                  {previewStory.viewsCount || 0} 次观看
                </span>
                <span className="text-[11px] text-white/60">
                  {previewStory.pinned ? "📌 主页置顶" : "常规动态"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default StoriesView;
